import { useState } from 'react';
import { 
  FormControl, InputLabel, Select, 
  TextField, FormHelperText, Stack, 
  MenuItem, Box, Typography, 
} from "@mui/material";
import { Button } from "@gisce/oficina-virtual-components";
import UploadButton from "../../UploadButton";
import { data_create_api_resource } from "../../../utils/http_functions";
import useReclamATCTipus from '../hooks/useReclamATCTipus';
import ContractSelectField from '@/components/form-fields/ContractSelectField/components/ContractSelectField';
import { useTranslation } from 'react-i18next';
import { ContractSelect } from '@/components/form-fields/ContractSelectField/types/contractSelectFields';
import useAuth from '@/components/Authentication/hooks/useAuth';
import { z } from "zod";
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from "react-hook-form";

const STATUS = {
  FORM: 0,
  SENDING: 1,
  SENT: 2,
};

const ReclamATC = () => {

  const { t } = useTranslation();
  const { token } = useAuth();

  const {
    availableTipus,
    availableSubtipus,
    setSelectedTipus,
    setSelectedSubtipus
  } = useReclamATCTipus();
  
  const [status, setStatus] = useState(STATUS.FORM);
  const [reclamacionId, setReclamacionId] = useState(null);

  const ReclamATCSchema = z.object({
    contract: (z.object({
      id: z.number(),
      name: z.string(),
      type: z.enum(["electricity", "gas"]),
    }) satisfies z.ZodType<ContractSelect>)
    .nullable()
    .refine((val) => val !== null && val !== undefined, {
      message: t('common:text.required_field'),
    }),
    tipus: z.string().nonempty(t('common:text.required_field')),
    subtipus: z.string().nonempty(t('common:text.required_field')),
    descripcio: z.string().nonempty(t('common:text.required_field')),
    attachment: z.object({
      name: z.string().nullable(),
      content: z.string().nullable(),
    }).nullable()
  })

  type ReclamATCForm = z.infer<typeof ReclamATCSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm<ReclamATCForm>({
    resolver: zodResolver(ReclamATCSchema),
    defaultValues: {
      contract: undefined,
      tipus: "",
      subtipus: "",
      descripcio: "",
      attachment: null,
    }
  });

  const onSubmit = async (data: ReclamATCForm) => {
    setStatus(STATUS.SENDING);
    try {
      const postData = {
        contract: data.contract!.name,
        tipus: data.tipus,
        subtipus: data.subtipus,
        comments: data.descripcio,
        attachment: data.attachment,
      };

      const result = await data_create_api_resource(token, `contracts/${data.contract!.name}/atc/`, postData);
      setStatus(STATUS.SENT);
      setReclamacionId(result.data.result);
    } catch (error) {
      console.error("Error submitting form:", error);
      setStatus(STATUS.FORM);
    }
  };

  const renderDone = () => (
    <div>
      {t('common:text.atc_success')} <code>#{reclamacionId}</code>.
    </div>
  );

  const renderForm = () => {
    const isSending = status === STATUS.SENDING;

    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction="column" spacing={2}>
          <Controller
            name="contract"
            control={control}
            render={({ field }) => (
              <ContractSelectField
                value={field.value}
                onChange={(val: ContractSelect) => field.onChange(val)}
                hideIfEmpty={false}
              />
            )}
          />

          <Controller
            name="tipus"
            control={control}
            render={({ field }) => (
              <>
                {!!availableTipus.length && (
                  <FormControl style={{ marginBottom: 10 }} fullWidth={true}>
                    <InputLabel shrink>
                      {t('common:text.atc_contract_type')}
                    </InputLabel>
                    <Select
                      {...field}
                      fullWidth
                      value={field.value || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(value);
                        setSelectedTipus(value);
                        setSelectedSubtipus(undefined)
                        setValue('subtipus', '', {
                          shouldValidate: true,
                          shouldDirty: true,
                        })
                      }}
                      error={!!errors.tipus}
                    >
                      {availableTipus.map((t) => (
                        <MenuItem key={t[0]} value={t[0]}>
                          {t[1]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {errors.tipus && (
                  <FormHelperText error id="reclam-tipus-error-text">
                    {errors.tipus.message}
                  </FormHelperText>
                )}
              </>
            )}
          />

          {!!availableSubtipus.length &&
            <Controller
              name="subtipus"
              control={control}
              render={({ field }) => (
                <>
                  <FormControl fullWidth>
                    <InputLabel shrink>
                      {t("common:text.atc_contract_subtype")}
                    </InputLabel>
                    <Select
                      {...field}
                      fullWidth
                      value={field.value || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(value);
                        setSelectedSubtipus(
                          availableSubtipus.find((st) => st.name === value)
                        );
                      }}
                      error={!!errors.subtipus}
                    >
                      {availableSubtipus.map((st) => (
                        <MenuItem key={st.name} value={st.name}>
                          {st.desc}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {errors.subtipus && (
                    <FormHelperText error id="reclam-subtipus-error-text">
                      {errors.subtipus.message}
                    </FormHelperText>
                  )}
                </>
              )}
            />
          }

          <Controller
            name="descripcio"
            control={control}
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  label={t('common:text.atc_description')+ "*"}
                  multiline={true}
                  minRows={5}
                  fullWidth={true}
                  error={!!errors.descripcio}
                />
                {errors?.descripcio?.message && (
                  <FormHelperText error={true} id="reclam-error-text">
                    {errors.descripcio.message}
                  </FormHelperText>
                )}
              </>
            )}
          />

          <Controller
            name="attachment"
            control={control}
            render={({ field }) => (
              <UploadButton
                value={field.value}
                label={t('common:text.atc_attachment')}
                onChange={(file: string) => field.onChange(file)}
              />
            )}
          />
          {errors.attachment && (
            <FormHelperText error>{errors.attachment.message}</FormHelperText>
          )}

          <Button
            color={'primary'}
            variant={'contained'}
            type="submit"
            loading={isSending}
          >
            {t('common:text.atc_send')}
          </Button>
        </Stack>
      </form>
    );
  };

  return (
    <div>
      <Box mb={2}>
        <Typography variant="h5">
          {t('common:text.atc_title')}
        </Typography>
      </Box>
      {(status === STATUS.FORM || status === STATUS.SENDING) && renderForm()}
      {status === STATUS.SENT && renderDone()}
    </div>
  );
};

export default ReclamATC;
